import { HEADER_BYTES, MALFORMED, OK, assertField, checkHeader, putHeader, putU16, readU16, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { EFFECT_ENTRY_BYTES, type EffectEntry } from "./effects.ts";
import { MAX_U16, NO_HANDLE } from "./limits.ts";

/**
 * The `targetEffects` frame (ADR-079's amendment for P2.2-T4, PROTOCOL.md 5.13): server to client, the visible effects
 * the player's selected target bears, each as the owner's `effects` frame carries it (its number, its ticks left counted
 * from the tick it is sent, its stacks), after the target's handle, so a page drops a frame for a target it no longer
 * holds. It reaches only the player holding that target: in the tick the target is set, changed or dropped, and in any
 * tick the target's visible effects were gained, refreshed, stacked or ended. A map server writes it into a buffer its
 * session allocates at join (`putTargetEffectEntry`, `putTargetEffectsHeader`), so a send allocates nothing; the
 * decoder never throws (ADR-030).
 */

/**
 * Bytes before the first effect: the frame header, the target's handle and the count.
 */
export const TARGET_EFFECTS_HEADER_BYTES = HEADER_BYTES + 3;

const MAX_COUNT = 255;
const MAX_BYTE = 255;

/**
 * A decoded `targetEffects` frame, reused across frames: the target it describes (`NO_HANDLE` for none), how many
 * effects it held and the effects, the first `count` of `effects` valid.
 */
export interface TargetEffectsRecord {
  target: number;
  count: number;
  readonly effects: EffectEntry[];
}

/**
 * A fresh record for `decodeTargetEffects` to fill.
 *
 * @returns An empty record naming no target.
 */
export function newTargetEffectsRecord(): TargetEffectsRecord {
  return { target: NO_HANDLE, count: 0, effects: [] };
}

/**
 * Bytes of a frame of a number of effects.
 *
 * @param count The effects.
 * @returns `6 + 5 * count`.
 */
export function targetEffectsBytes(count: number): number {
  return TARGET_EFFECTS_HEADER_BYTES + EFFECT_ENTRY_BYTES * count;
}

/**
 * Writes one effect at its place in a frame through plain byte stores. The caller has sized the buffer and checked the
 * fields' widths.
 *
 * @param buffer The frame.
 * @param index The effect's place, from 0.
 * @param effect The effect's number.
 * @param ticksLeft Its ticks left.
 * @param stacks Its stacks.
 */
export function putTargetEffectEntry(buffer: Uint8Array, index: number, effect: number, ticksLeft: number, stacks: number): void {
  const at = TARGET_EFFECTS_HEADER_BYTES + EFFECT_ENTRY_BYTES * index;
  putU16(buffer, at, effect);
  putU16(buffer, at + 2, ticksLeft);
  buffer[at + 4] = stacks;
}

/**
 * Writes a frame's header once its effects are in place.
 *
 * @param buffer The frame.
 * @param target The target's handle, `NO_HANDLE` for none.
 * @param count The effects written, 0 to 255.
 * @returns The frame's length in bytes.
 */
export function putTargetEffectsHeader(buffer: Uint8Array, target: number, count: number): number {
  putHeader(buffer, MessageType.TargetEffects);
  putU16(buffer, HEADER_BYTES, target);
  buffer[HEADER_BYTES + 2] = count;
  return targetEffectsBytes(count);
}

/**
 * Encodes a `targetEffects` frame into a caller-owned buffer, checking every field.
 *
 * @param buffer Destination, at least `targetEffectsBytes(effects.length)` long.
 * @param target The target's handle, `NO_HANDLE` for none.
 * @param effects 0 to 255 effects.
 * @returns Bytes written.
 * @throws Error when there are more than 255, the buffer is too small, or a field does not fit its width.
 */
export function encodeTargetEffects(buffer: Uint8Array, target: number, effects: readonly Readonly<EffectEntry>[]): number {
  if (effects.length > MAX_COUNT) {
    throw new Error(`target effect count ${effects.length} is more than ${MAX_COUNT}`);
  }
  const length = targetEffectsBytes(effects.length);
  if (buffer.length < length) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${length}-byte targetEffects frame`);
  }
  assertField(target, MAX_U16, `targetEffects target ${target}`);
  for (let index = 0; index < effects.length; index += 1) {
    const entry = effects[index] as Readonly<EffectEntry>;
    assertField(entry.effect, MAX_U16, `targetEffects effect ${entry.effect}`);
    assertField(entry.ticksLeft, MAX_U16, `targetEffects ticksLeft ${entry.ticksLeft}`);
    assertField(entry.stacks, MAX_BYTE, `targetEffects stacks ${entry.stacks}`);
    putTargetEffectEntry(buffer, index, entry.effect, entry.ticksLeft, entry.stacks);
  }
  return putTargetEffectsHeader(buffer, target, effects.length);
}

/**
 * Decodes a `targetEffects` frame into a record. Never throws: a frame shorter than its header, a length other than
 * `6 + 5 * count` or another type is malformed, and another version a mismatch, leaving the record untouched. Every
 * value of every field is well formed.
 *
 * @param frame The whole frame.
 * @param out Record to fill.
 * @returns The verdict.
 */
export function decodeTargetEffects(frame: Uint8Array, out: TargetEffectsRecord): DecodeVerdict {
  if (frame.length < TARGET_EFFECTS_HEADER_BYTES) {
    return MALFORMED;
  }
  const count = frame[HEADER_BYTES + 2] as number;
  const header = checkHeader(frame, MessageType.TargetEffects, targetEffectsBytes(count));
  if (header.kind !== "ok") {
    return header;
  }
  const effects = out.effects;
  for (let index = 0; index < count; index += 1) {
    const at = TARGET_EFFECTS_HEADER_BYTES + EFFECT_ENTRY_BYTES * index;
    let entry = effects[index];
    if (entry === undefined) {
      entry = { effect: 0, ticksLeft: 0, stacks: 0 };
      effects.push(entry);
    }
    entry.effect = readU16(frame, at);
    entry.ticksLeft = readU16(frame, at + 2);
    entry.stacks = frame[at + 4] as number;
  }
  out.target = readU16(frame, HEADER_BYTES);
  out.count = count;
  return OK;
}
